import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Instagram } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_neon-fix-dark/artifacts/j4zm2tv7_logo%20vaz%20tech.jpeg";
const WHATSAPP_NUMBER = "5521980277322";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Início', path: '/' },
    { name: 'Celular', path: '/celular' },
    { name: 'Notebook', path: '/notebook' },
    { name: 'Smartwatch', path: '/smartwatch' },
    { name: 'Computador', path: '/computador' },
    { name: 'Web Design', path: '/webdesign' },
  ];

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Top Bar */}
      <div className="hidden md:block bg-[#0A0A0A] border-b border-white/5 py-2">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-xs text-[#94A3B8] font-space">
          <div className="flex items-center gap-6">
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} className="flex items-center gap-2 hover:text-[#00F0FF] transition-colors" data-testid="topbar-phone">
              <Phone size={12} />
              <span>(21) 98027-7322</span>
            </a>
            <a href="mailto:vaztechbarra@gmail.com" className="flex items-center gap-2 hover:text-[#00F0FF] transition-colors" data-testid="topbar-email">
              <Mail size={12} />
              <span>vaztechbarra@gmail.com</span>
            </a>
          </div>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <MapPin size={12} />
              Barra da Tijuca, RJ
            </span>
            <a href="https://instagram.com/vaztech_barra" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#00F0FF] transition-colors" data-testid="topbar-instagram">
              <Instagram size={12} />
              @vaztech_barra
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav 
        className={`fixed top-0 md:top-[36px] left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#050505]/95 backdrop-blur-xl' : 'bg-transparent'
        } border-b border-white/5`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="navbar-logo">
              <img 
                src={LOGO_URL} 
                alt="Vaz Tech Solutions" 
                className="h-12 md:h-14 object-contain"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-5 xl:gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-xs xl:text-sm uppercase tracking-wider font-rajdhani font-semibold transition-colors whitespace-nowrap ${
                    location.pathname === link.path ? 'text-[#00F0FF]' : 'text-white hover:text-[#00F0FF]'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block flex-shrink-0 ml-4">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Vim pelo site e gostaria de um orçamento.`}
                target="_blank"
                rel="noopener noreferrer"
                className="cyber-btn text-xs xl:text-sm px-4 xl:px-6"
                data-testid="navbar-cta-whatsapp"
              >
                Orçamento
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden text-white p-2"
              data-testid="mobile-menu-button"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mobile-menu border-t border-white/5"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-6 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block text-lg font-rajdhani font-semibold uppercase tracking-wider ${
                      location.pathname === link.path ? 'text-[#00F0FF]' : 'text-white'
                    }`}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-4 border-t border-white/10">
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Vim pelo site e gostaria de um orçamento.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-btn w-full justify-center text-sm"
                    data-testid="mobile-cta-whatsapp"
                  >
                    <Phone size={18} />
                    Falar no WhatsApp
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
